<script setup lang="ts">
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type {
    IndexerTitleProperties as IndexerTitlePropsType,
    IndexerType,
  } from '../../../model/form.types';
  import * as z from 'zod';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  type FormValues = {
    indexerType?: IndexerType;
    value?: string;
    label?: string;
    labelPosition?: 'top' | 'left';
  };

  const INDEXER_OPTIONS: { value: IndexerType; label: string }[] = [
    { value: 'USER_DEFINED', label: 'Definido pelo usuário' },
    { value: 'SEQUENTIAL', label: 'Sequencial' },
    { value: 'NON_SEQUENTIAL', label: 'Não sequencial' },
    { value: 'SEQUENTIAL_WITH_DATE', label: 'Sequencial com data' },
    { value: 'OS_OF', label: 'Indexação OS/OF' },
  ];

  const { values } = usePropertyForm<FormValues, IndexerTitlePropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      indexerType: z
        .enum(['USER_DEFINED', 'SEQUENTIAL', 'NON_SEQUENTIAL', 'SEQUENTIAL_WITH_DATE', 'OS_OF'])
        .optional(),
      value: z.string().max(FORM_FIELD_CHARACTER_LIMITS.VALUE).optional(),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      labelPosition: z.enum(['top', 'left']).optional(),
    }),
    extractInitialValues: (properties: IndexerTitlePropsType | undefined): FormValues => ({
      indexerType: properties?.indexerType ?? 'USER_DEFINED',
      value: properties?.value ?? '',
      label: properties?.label ?? '',
      labelPosition: properties?.labelPosition ?? 'top',
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: IndexerTitlePropsType) => {
      const indexerType = values.indexerType ?? 'USER_DEFINED';
      return {
        properties: {
          ...currentProperties,
          indexerType,
          value: indexerType === 'USER_DEFINED' ? values.value || undefined : undefined,
          label: values.label || undefined,
          labelPosition: values.labelPosition ?? 'top',
        },
      };
    },
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ value, handleChange }"
        name="indexerType"
      >
        <FormItem class="flex flex-col gap-3">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Indexador:
          </FormLabel>
          <div class="flex flex-col gap-3">
            <label
              v-for="option in INDEXER_OPTIONS"
              :key="option.value"
              class="inline-flex items-center cursor-pointer gap-2"
            >
              <input
                type="radio"
                :name="`indexerType-${props.elementId ?? 'unknown'}`"
                :value="option.value"
                :checked="value === option.value"
                class="hidden peer"
                @change="handleChange(option.value)"
              >
              <span
                class="w-5 h-5 inline-block rounded-full border border-light-gray-xx bg-white-01 peer-checked:border-[6px] peer-checked:border-[var(--theme-secondary-ink)] shrink-0"
              />
              <span class="text-sm text-gray-11">{{ option.label }}</span>
            </label>
          </div>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div
      v-if="values.indexerType === 'USER_DEFINED'"
      class="border-b border-gray-02 py-4"
    >
      <FormField
        v-slot="{ componentField }"
        name="value"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Valor
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o valor"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.VALUE"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
